<template lang="html">
  <div>

    <p v-if="loading"><i class="far fa-fw fa-pulse fa-spinner"></i></p>

    <p v-if="!hasPhoneMFAFactor" class="alert alert-app">
      <span><strong>Upgrade your account security to enable all features.</strong> To set or view Patient Numbers for members, you must have MFA enabled. <router-link to="/profile" exact-active-class="active">Set Up MFA on Your User Profile</router-link></span>
    </p>

    <div v-if="member && isUserAllowedToView" class="member-meta">
      <p class="dark-color snug">Member</p>
      <h3>{{ member.name_first }} {{ member.name_last }}</h3>
      <p v-if="isPracticeUser && hasPhoneMFAFactor && hasOdConnection">
        <span v-if="!editPatNum">
          <strong>Patient Number:</strong><span v-if="patNum"> {{ patNum }}</span> <button class="btn btn-link" @click="editPatNum=true"><i class="far fa-fw fa-pencil"></i></button>
        </span>
        <span v-if="editPatNum">
          <span class="snug"><strong>Add Patient Number</strong></span>
          <span class="form-pat-num d-flex">
            <input v-model.number="patNum" name="patNum" class="form-control" placeholder="Enter Patient Number" type="number" @keyup.enter="savePatNum"/>
            <i v-if="loadingPatNum" class="far fa-spinner-third fa-spin fa-2x"></i>
            <button class="btn btn-icon" @click="savePatNum"><i class="far fa-pencil"></i> Save</button>
            <button class="btn btn-link" @click="editPatNum=false">cancel</button>
          </span>
          <list-errors :errors="errors"></list-errors>
        </span>
      </p>
      <p v-if="guarantorAccount && patientUserId && guarantorAccount.private.members[patientUserId]"><strong>Status: </strong><em>{{ memberStatusPretty }}</em></p>
      <p v-if="guarantorProps"><strong>Guarantor: </strong><router-link :to="'/guarantor-profile/' + this.member.guarantor_account_id">{{ guarantorProps.name_first }} {{ guarantorProps.name_last }}</router-link></p>
    </div>

    <div v-if="plan && isUserAllowedToView" class="plan-details ha-plans">
      <h4>Membership Plan: <strong>{{ plan.name }}</strong></h4>
      <p><strong>Plan Price: </strong> {{ formatStripePrice(plan.prices[priceId].amount) }}/{{ planPriceInterval }}</p>
      <p><strong>Assured Preventive Health Services</strong></p>
      <div v-html="$sanitize(plan.benefits_preventative)"></div>
      <p><strong>Health Restorative Services</strong></p>
      <div v-html="$sanitize(plan.benefits_restorative)"></div>
      <p><strong>Special Additions</strong></p>
      <div v-html="$sanitize(plan.benefits_special)"></div>
      <p><strong>Who is this good for?</strong></p>
      <div v-html="$sanitize(plan.benefits_patient_type)"></div>
    </div>

    <div v-if="!loading && !isUserAllowedToView">
      <p><em>Sorry, you don't have permission to view this page.</em></p>
    </div>

  </div>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import ListErrors from '../components/ListErrors.vue'

export default {
  props: ['patientUserId'],
  components: {
    ListErrors, 
  },
  data () {
    return {
      loading: true,
      member: null,
      guarantorAccount: null,
      guarantorProps: null,
      // use computed props instead, sometimes these get undefined
      // planId: null,
      // priceId: null,
      plan: null,
      editPatNum: false,
      patNum: null,
      errors: [],
      loadingPatNum: false,
    }
  },
  computed: {
    ...mapGetters(['user_props','user','isPracticeUser','hasOdConnection']),
    hasPhoneMFAFactor() {
      if(this.user && this.user.multiFactor && this.user.multiFactor.enrolledFactors && this.user.multiFactor.enrolledFactors.some(f => f.factorId == "phone")) {
        return true;
      }
      return false;
    },
    memberStatusPretty () {
      let status = this.guarantorAccount.private.members[this.patientUserId].subscription_status;
      if(status == "pastDue") {
        return "past due";
      }
      if(!status && !this.guarantorAccount.private.members[this.patientUserId].subscription_id) {
        return "awaiting payment";
      }
      return this.guarantorAccount.private.members[this.patientUserId].subscription_status;
    },
    // verify logged in user owns account this member belongs to
    isUserAllowedToView () {
      if(this.user_props && this.member && this.user_props.owned_account_id && this.member.practice_account_id && this.user_props.owned_account_id == this.member.practice_account_id) {
        return true;
      }
      return false;
    },
    planPriceInterval () {
      if(this.plan && this.priceId) {
        let interval = this.plan.prices[this.priceId].interval;
        if(interval == 'month') {
          return 'mo';
        }
        if(interval == 'year') {
          return 'yr';
        }
      } else {
        return ''
      }
    },
    planId () {
      if(this.guarantorAccount && this.guarantorAccount.private && this.guarantorAccount.private.members && this.guarantorAccount.private.members[this.patientUserId]) {
        return this.guarantorAccount.private.members[this.patientUserId].plan_id
      }
      return null;
    },
    priceId () {
      if(this.guarantorAccount && this.guarantorAccount.private && this.guarantorAccount.private.members && this.guarantorAccount.private.members[this.patientUserId]) {
        return this.guarantorAccount.private.members[this.patientUserId].plan_price_id
      }
      return null;
    },
  },
  watch: {
    // load member on page reload
    patientUserId () {
      this.loadMember();
    },
    member () {
      if(this.member) {
        // when member set, load guarantorAccount
        this.loading = true;
        Vue.db.ref(`guarantor_accounts/${this.member.guarantor_account_id}`)
          .once('value')
          .then(snapshot => {
            if(snapshot.val()) {
              this.guarantorAccount = snapshot.val();
              // use computed props instead, sometimes these get undefined
              // this.planId = snapshot.val().private.members[this.patientUserId].plan_id;
              // this.priceId = snapshot.val().private.members[this.patientUserId].plan_price_id;
              this.loading = false;
            } else {
              console.log("error loading guarantorAccount");
            }
          })
          .catch(err => console.log(err));
        // try to load PatNum from Firestore
        this.loadPatNum();
      }
    },
    // when guarantorAccount set, load plan & guarantor props
    guarantorAccount () {
      if(this.guarantorAccount && this.planId && this.priceId) {
        // load guarantor props to get name
        Vue.db.ref(`user_props/${this.guarantorAccount.private.guarantor_uid}`)
          .once('value')
          .then(snapshot => {
            if(snapshot.val()) {
              this.guarantorProps = snapshot.val();
              this.loading = false;
            } else {
              console.log("error loading guarantorProps");
            }
          })
          .catch(err => console.log(err));
        // load plan to get name and benefits
        Vue.db.ref(`plans/${this.planId}`)
          .once('value')
          .then(snapshot => {
            if(snapshot.val()) {
              this.plan = snapshot.val();
              this.loading = false;
            } else {
              console.log("error loading plan");
            }
          })
          .catch(err => console.log(err));
      }
    },
  },
  methods: {
    loadPatNum () {
      if(!this.isPracticeUser || !this.hasPhoneMFAFactor || !this.user || !this.user.uid || !this.user_props || !this.user_props.owned_account_id || !this.member || !this.patientUserId || !this.member.guarantor_account_id || !this.member.guarantor_uid) {
        return;
      }
      const memberDocPath = `owners/${this.user.uid}/accounts/${this.user_props.owned_account_id}/members/${this.patientUserId}`;
      // console.log({memberDocPath});
      // save to firestore
      Vue.firestore
        .doc(memberDocPath)
        .get()
        .then(docSnap => {
          if(docSnap && docSnap.exists && docSnap.data().pat_num) {
            this.patNum = docSnap.data().pat_num;
            return true;
          }
          return false;
        })
        .catch(err => console.log(err));
    },
    savePatNum () {
      this.loadingPatNum = true;
      this.errors = [];
      // validate patNum
      console.log('patNum',this.patNum);
      if(!this.patNum || !this.patNum.toString().length > 10 || !Number.isInteger(this.patNum)) {
        this.errors.push("Invalid Patient Number (PatNum)");
      }
      // validate required data exists
      if(!this.isPracticeUser || !this.hasPhoneMFAFactor || !this.user || !this.user.uid || !this.user_props || !this.user_props.owned_account_id || !this.member || !this.patientUserId || !this.member.guarantor_account_id || !this.member.guarantor_uid) {
        this.errors.push("You're missing the permissions to save Patient Numbers.");
      }
      if(this.errors.length) {
        this.loadingPatNum = false;
        return;
      }
      const memberUpdate = {
        pat_num: this.patNum,
        guarantor_uid: this.member.guarantor_uid,
        guarantor_account_id: this.member.guarantor_account_id,
        member_id: this.patientUserId,
        practice_account_id: this.user_props.owned_account_id,
        practice_account_owner_uid: this.user.uid,
      };
      console.log({memberUpdate});
      const memberDocPath = `owners/${this.user.uid}/accounts/${this.user_props.owned_account_id}/members/${this.patientUserId}`;
      console.log({memberDocPath});
      // save to firestore
      Vue.firestore
        .doc(memberDocPath)
        .set(memberUpdate)
        .then(() => {
          console.log('saved Member PatNum');
          this.editPatNum = false;
        })
        .catch(err => {
          console.log(err);
          this.errors = [`${err.message}`];
        })
        .then(() => {
          this.loadingPatNum = false;
        })
    },
    formatStripePrice(price) {
      return `$${price/100}`;
    },
    loadMember() {
      if(this.patientUserId && !this.member) {
        this.loading = true;
        Vue.db.ref(`members/${this.patientUserId}`)
          .once('value')
          .then(snapshot => {
            if(snapshot.val()) {
              this.member = snapshot.val();
              this.loading = false;
            } else {
              console.log("error loading member");
            }
          })
          .catch(err => console.log(err));
      }
    },
  },
  mounted () {
    // load member on router navigation
    this.loadMember();
  },
}
</script>

<style lang="less" scoped>
@import "../assets/less/variables.less";

.member-meta {
  margin-bottom: 50px;

  > p {
    margin-bottom: 10px;
  }
}
.plan-details {
  h4 {
    margin: 0 0 10px;
  }
}
.form-pat-num {
  margin-bottom: 10px;
}
.form-pat-num .form-control,
.form-pat-num .btn {
  margin-left: 15px;
}
.form-pat-num .form-control {
  margin-bottom: 0;
  margin-left: 0;
}
.form-pat-num .btn:last-child {
  margin: 0;
}
.form-pat-num .btn {
  min-width: 75px;
}
@media (min-width: @screen-md-min) {
  .form-pat-num {
    max-width: 50%;
  }
}
// @media (min-width: @screen-lg-min) {
//   .form-pat-num {
//     max-width: 33%;
//   }
// }
</style>
